import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Check, CheckCheck, Clock, MessageSquarePlus, MessageSquareOff, Pin, MessageCircleReply, User, Server, Users } from 'lucide-react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { ConversaResumo } from '../types/conversa';
import { renderTextWithLinks } from '../utils/renderTextWithLinks';
import { getUltimaMensagemComRascunho, getFileUrl, getInitials, formatUltimaInteracao } from '../utils/dashboardHelpers';
import { TagBadge } from './ui/tag-badge';

interface ConversaListItemProps {
  conversa: ConversaResumo;
  chatConfig: any;
  user: any;
  startingAttendance: boolean;
  onOpenConversa: (conversa: ConversaResumo) => void;
  onStartAtendimento: (conversa: ConversaResumo) => void;
  onFinishAtendimento: (conversa: ConversaResumo) => void;
  onReopenAtendimento: (conversa: ConversaResumo) => void;
  onTransferClick: (conversa: ConversaResumo) => void;
  onPinToggle: (conversa: ConversaResumo) => void;
}

const ConversaListItem: React.FC<ConversaListItemProps> = ({
  conversa,
  user,
  chatConfig,
  startingAttendance,
  onOpenConversa,
  onStartAtendimento,
  onFinishAtendimento,
  onReopenAtendimento,
  onTransferClick,
  onPinToggle,
}) => {
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);

  const renderStatusIcon = () => {
    const msg = conversa.ultimaMensagem;
    if (!msg) return null;
    switch (msg.statusEntrega) {
      case 'enviada':
        return <Check size={16} className="text-gray-400 dark:text-gray-500 mr-1 flex-shrink-0" />;
      case 'entregue':
        return <CheckCheck size={16} className="text-gray-400 dark:text-gray-500 mr-1 flex-shrink-0" />;
      case 'lida':
      case 'reproduzida':
        return <CheckCheck size={16} className="text-blue-500 mr-1 flex-shrink-0" />;
      default:
        if (msg.statusEntrega === 'pendente') {
          return <Clock size={16} className="text-gray-400 dark:text-gray-500 mr-1 flex-shrink-0" />;
        }
        return null;
    }
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX - 2,
          mouseY: event.clientY - 4,
        }
        : null,
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleMenuItemClick = (action: () => void) => {
    handleClose();
    action();
  };

  return (
    <>
      <div
        key={conversa.id}
        className={`relative hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer flex items-${conversa.ticketTags?.length ? 'start' : 'center'} p-2 min-h-[120px] transition-colors duration-150 rounded-lg shadow-sm mb-2
          ${conversa.ultimaMensagem?.remetenteTipo !== 'atendente'
            ? 'before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1.5 before:bg-orange-400 before:rounded-l-lg border border-transparent'
            : 'border border-transparent'
          }
        `}
        style={{
          borderBottom: '1px solid var(--tw-border-opacity, 1) var(--tw-border-color, #f3f4f6)',
        }}
        onClick={(e) => {
          e.preventDefault();
          onOpenConversa(conversa);
        }}
        onContextMenu={handleContextMenu}
      >
        <div className="flex relative">
          <Avatar className="h-12 w-12 flex-shrink-0">
            <AvatarImage src={getFileUrl(conversa.avatar)} alt={conversa.nome} />
            <AvatarFallback className="bg-gray-300 dark:bg-slate-600 text-white">{getInitials(conversa.nome)}</AvatarFallback>
          </Avatar>
          <div className="absolute top-3/4 flex justify-center w-full">
            {conversa.ultimaMensagem?.isGrupo !== undefined && (
              <span className={`text-xs font-medium px-1 py-0.5 rounded ${conversa.ultimaMensagem.isGrupo
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                } mx-auto`}>
                {conversa.ultimaMensagem.isGrupo ? 'Grupo' : 'Privado'}
              </span>
            )}
          </div>
        </div>
        <div className="ml-3 transition-all duration-300 flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3
              className="font-medium truncate text-base text-gray-900 dark:text-gray-100 flex items-center gap-1"
              title={conversa.nome}
            >
              {conversa.fixada && <Pin size={14} className="text-blue-500 flex-shrink-0 fill-blue-500" />}
              {conversa.nome.length > 16 ? `${conversa.nome.slice(0, 16)}...` : conversa.nome}
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
              {formatUltimaInteracao(conversa)}
            </span>
          </div>
          <div className="flex items-start justify-between mt-1">
            <div className="flex-1 min-w-0">
              {conversa.ultimaMensagem?.isGrupo && conversa.ultimaMensagem?.remetenteContato ? (
                <div className="flex items-center gap-2 mb-1">
                  <Avatar className="h-6 w-6 flex-shrink-0">
                    <AvatarImage src={getFileUrl(conversa.ultimaMensagem.remetenteContato.avatarUrl)} alt={conversa.ultimaMensagem.remetenteContato.nome} />
                    <AvatarFallback className="bg-gray-300 dark:bg-slate-600 text-white text-xs">
                      {getInitials(conversa.ultimaMensagem.remetenteContato.nome)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                        {conversa.ultimaMensagem.remetenteContato.nome || 'Desconhecido'}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {conversa.ultimaMensagem.remetenteContato.telefone}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {renderStatusIcon()}
                      {(() => {
                        const mensagemData = getUltimaMensagemComRascunho(conversa);
                        if (!mensagemData.texto) {
                          return 'Nenhuma mensagem recente';
                        }

                        const displayText = mensagemData.texto.length > 30
                          ? mensagemData.texto.slice(0, 30) + '...'
                          : mensagemData.texto;

                        return mensagemData.isRascunho ? (
                          <>
                            <span className="font-bold text-gray-600 text-xs text-green-600 dark:text-green-400 mr-1">Rascunho:</span>
                            {renderTextWithLinks(displayText, true)}
                          </>
                        ) : (
                          <span className="text-xs text-gray-600">
                            {renderTextWithLinks(displayText, true, conversa.ultimaMensagem?.tipo, conversa.ultimaMensagem?.remetenteTipo)}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate flex items-center">
                  {renderStatusIcon()}
                  {(() => {
                    const mensagemData = getUltimaMensagemComRascunho(conversa);
                    if (!mensagemData.texto) {
                      return 'Nenhuma mensagem recente';
                    }

                    const displayText = mensagemData.texto.length > 35
                      ? mensagemData.texto.slice(0, 35) + '...'
                      : mensagemData.texto;

                    return mensagemData.isRascunho ? (
                      <>
                        <span className="font-bold text-green-600 dark:text-green-400 mr-1">Rascunho:</span>
                        {renderTextWithLinks(displayText, true)}
                      </>
                    ) : (
                      renderTextWithLinks(displayText, true, conversa.ultimaMensagem?.tipo, conversa.ultimaMensagem?.remetenteTipo)
                    );
                  })()}
                </p>
              )}
            </div>
            {conversa.naoLidas > 0 && (
              <span className="bg-green-500 text-white text-xs font-semibold rounded-full px-1.5 py-1 text-[11px] flex items-center justify-center ml-2 flex-shrink-0">
                {conversa.naoLidas}
              </span>
            )}
          </div>

          {(conversa.statusTicket === 'aberto' || conversa.statusTicket === 'fechado' || conversa.statusTicket === 'pendente') && (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex flex-col gap-1">
                {(conversa.atendente || conversa.instancia || conversa.fila) && (
                  <div className="flex items-center gap-2">
                    {conversa.atendente && (
                      <span className="flex items-center gap-1">
                        <User size={12} />
                        {conversa.atendente.nome}
                      </span>
                    )}
                    {conversa.atendente && (conversa.instancia || conversa.fila) && (
                      <span className="text-gray-400">|</span>
                    )}
                    {conversa.instancia && (
                      <span className="flex items-center gap-1">
                        <Server size={12} />
                        {conversa.instancia.nome}
                      </span>
                    )}
                    {conversa.instancia && conversa.fila && (
                      <span className="text-gray-400">|</span>
                    )}
                    {conversa.fila && (
                      <span className="flex items-center gap-1">
                        <Users size={12} />
                        {conversa.fila.nome}
                      </span>
                    )}
                  </div>
                )}
                {conversa.ticketTags && conversa.ticketTags.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {conversa.ticketTags.slice(0, 3).map((ticketTag: any) => (
                      <TagBadge
                        key={`ticket-${ticketTag.tagId}`}
                        titulo={ticketTag.tag.titulo}
                        cor={ticketTag.tag.cor}
                        prioridade={ticketTag.tag.prioridade}
                      />
                    ))}
                    {conversa.ticketTags.length > 3 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        +{conversa.ticketTags.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          {conversa.status === 'pendente' && (
            <>
              <MenuItem onClick={() => handleMenuItemClick(() => onStartAtendimento(conversa))} disabled={startingAttendance}>
                <MessageSquarePlus size={20} className="mr-2" />
                {startingAttendance ? 'Iniciando...' : 'Iniciar atendimento'}
              </MenuItem>
              {(user.role === 'Admin' || user.role === 'SuperAdmin' || chatConfig?.usuarioPodeFinalizarTicket) && (
                <MenuItem onClick={() => handleMenuItemClick(() => onFinishAtendimento(conversa))}>
                  <MessageSquareOff size={20} className="mr-2" />
                  Finalizar atendimento
                </MenuItem>
              )}
            </>
          )}
          {conversa.status === 'aberto' && (
            <MenuItem onClick={() => handleMenuItemClick(() => onFinishAtendimento(conversa))}>
              <MessageSquareOff size={20} className="mr-2" />
              Finalizar atendimento
            </MenuItem>
          )}
          {conversa.status === 'fechado' && (
            <MenuItem onClick={() => handleMenuItemClick(() => onReopenAtendimento(conversa))}>
              <MessageSquarePlus size={20} className="mr-2" />
              Reabrir atendimento
            </MenuItem>
          )}
          {(!conversa.ultimaMensagem?.isGrupo || chatConfig?.permitirTransferenciaGrupos) && (
            <MenuItem onClick={() => handleMenuItemClick(() => onTransferClick(conversa))}>
              <MessageCircleReply size={20} className="mr-2" />
              Transferir atendimento
            </MenuItem>
          )}
          <MenuItem onClick={() => handleMenuItemClick(() => onPinToggle(conversa))}>
            <Pin size={20} className="mr-2" />
            {conversa.fixada ? 'Desafixar conversa' : 'Fixar conversa'}
          </MenuItem>
        </Menu>
      </div>
    </>
  );
};

export default ConversaListItem;
